/*
 * The pause / resume / cancel control bar. One component drives both the queue's
 * inline controls and the run-detail control bar.
 *
 * Each button is enabled strictly per the allowed transition for the run's
 * (status, control) — a terminal run disables everything; pausing a
 * non-pausable state is disabled with a reason; resume only lights up on the
 * parked states; cancel asks for confirmation (it's irreversible). Controls are
 * gated to operator via the auth `can("operate")` affordance — a viewer sees no
 * controls at all (the backend still enforces with 403/409).
 *
 * Mutations go through the shared `useRunControl(runId)` hook, which optimistic-
 * invalidates the run list + that run's detail on success (the refetch).
 */

import { useState } from "react";
import { Button } from "@mantine/core";
import { useAuth } from "../../auth/AuthContext";
import { useRunControl } from "../../api/queries";
import { errorMessage } from "../../api/errors";
import {
  controlAvailability,
  type ControlAction,
  type RunControlState,
} from "./transitions";
import { ConfirmDialog } from "./ConfirmDialog";
import styles from "./pipeline.module.css";

export interface ControlBarProps {
  runId: string;
  status: string;
  control: string;
  /** Compact = icon-light small buttons for the dense queue table. */
  size?: "sm" | "md";
  /** Surface a control error inline (run-detail). Queue stays quiet by default. */
  onError?: (message: string) => void;
}

export function ControlBar({
  runId,
  status,
  control,
  size = "md",
  onError,
}: ControlBarProps) {
  const { can, isSuperAdmin } = useAuth();
  const mutation = useRunControl(runId);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [confirmingForceCancel, setConfirmingForceCancel] = useState(false);

  // Viewers never see launch controls (backend enforces too).
  if (!can("operate")) return null;

  const runState: RunControlState = { status, control };
  const pending = mutation.isPending;
  const pendingAction = mutation.variables;

  const run = (action: ControlAction) => {
    mutation.mutate(action, {
      onError: (err) => onError?.(errorMessage(err)),
    });
  };

  // Our control sizes map onto Mantine's scale (sm → xs, md → sm).
  const mantineSize = size === "sm" ? "xs" : "sm";

  const button = (action: ControlAction, label: string, variant: "secondary" | "danger") => {
    const { enabled, reason } = controlAvailability(action, runState);
    const isThisPending = pending && pendingAction === action;
    const v =
      variant === "danger"
        ? { variant: "filled", color: "red" }
        : { variant: "default" };
    return (
      <Button
        key={action}
        size={mantineSize}
        variant={v.variant}
        color={v.color}
        disabled={!enabled || pending}
        loading={isThisPending}
        title={!enabled ? reason : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (action === "cancel") {
            setConfirmingCancel(true);
          } else if (action === "force-cancel") {
            setConfirmingForceCancel(true);
          } else {
            run(action);
          }
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className={styles.controls}>
      {button("pause", "Pause", "secondary")}
      {button("resume", "Resume", "secondary")}
      {/* Re-drive: retry a FAILED run from the stage that failed (idempotent —
          finalize reconciles against live Meta, so nothing double-launches).
          Rendered only on a FAILED run: it targets exactly one terminal state,
          so a disabled button everywhere else would be permanent noise. */}
      {controlAvailability("redrive", runState).enabled &&
        button("redrive", "Re-drive", "secondary")}
      {button("cancel", "Cancel", "danger")}
      {/* Super-admin escape hatch: force-abort a wedged run (e.g. stuck in
          "cancelling") regardless of state. Shown only to super-admins, only when
          the run is non-terminal. */}
      {isSuperAdmin &&
        controlAvailability("force-cancel", runState).enabled &&
        button("force-cancel", "Force cancel", "danger")}

      <ConfirmDialog
        open={confirmingCancel}
        title="Cancel this run?"
        body={
          <>
            Cancelling drains any in-flight batch to terminal, sweeps this run's
            orphaned adsets, surfaces partials, and aborts the run. This is
            irreversible — the run cannot be resumed afterward.
          </>
        }
        confirmLabel="Cancel run"
        loading={pending && pendingAction === "cancel"}
        onConfirm={() => {
          setConfirmingCancel(false);
          run("cancel");
        }}
        onCancel={() => setConfirmingCancel(false)}
      />

      <ConfirmDialog
        open={confirmingForceCancel}
        title="Force-cancel this run?"
        body={
          <>
            Force-cancel <strong>hard-aborts</strong> the run to ABORTED immediately,
            regardless of its current state — bypassing the cooperative drain. Use it
            only for a run wedged in an unrecoverable state (e.g. stuck in
            "cancelling"). Any in-flight work is not drained; the maintenance sweep
            backstops orphaned resources. This is irreversible.
          </>
        }
        confirmLabel="Force cancel"
        loading={pending && pendingAction === "force-cancel"}
        onConfirm={() => {
          setConfirmingForceCancel(false);
          run("force-cancel");
        }}
        onCancel={() => setConfirmingForceCancel(false)}
      />
    </div>
  );
}
